/**
 * Abstract representation of a map layer, which is a collection of geometry models *or* a branch-level collection of other map layers. Provides functionality that supports interaction with a layer.
 *
 * Provides backbone event functionality to inheritors, in addition to the following fields, events, and functions.
 * ## Fields
 * Name | Type | Description
 * --- | --- | ---
 * name | `string` | The name of this layer. Alias to `getName`.
 * children | `Array` | Gets the immediate child layers (of type `MapLayerBase`) of this layer. Alias to `getChildren`.
 * visible | `Boolean` | True if this layer is marked as visible, otherwise false. Alias to `isVisible` and `setVisible`.
 * layer | `esri/layers/GraphicsLayer` **or** `esri/layers/FeatureLayer | The ESRI layer object represented to this layer. May be null.
 * fields | `Array` | Gets the set of `Field` models that can be used to display item metadata in this layer. Alias to `getFields`.
 *
 * ## Events
 * Name | Description | Arguments
 * --- | --- | ---
 * visibility-changed | Fired whenever the visibility of this layer is changed. | A reference to this object and the current value of `visible`.
 * attach | Fired when the layer is attached to a map. | No arguments.
 *
 * ## Data Models vs Geometry Models
 * Each item in a layer is assumed to represent two sets of data. One set of data is the geometric representation of the object, whereas the other set of data is tabular - data that can easily be placed in a table. Concrete implementations should always yield the geometric model when operations are performed. The data model is the object which can be passed to fields returned by `getFields` to retrieve field values. The geometric and tabular models can be exchanged ad infinitum via the `dataForGeometry` and `geometryForData` functions.
 * @module app/models/map/base/MapLayerBase
 */

import * as _ from "underscore";
import * as $ from "jquery";
import * as extend from "backbone-extend-standalone";
import * as Backbone from "backbone";

/**
 * Constructor.
 * @param {object} options The options map.
 * @param {string} [options.name] The display name of this service.
 * @param {Boolean} [options.visible] The initial value of the `visible` field.
 * @param {app/Context} options.context The context associated with this layer.
 */
function MapLayerBase(options) {
  _.extend(this, Backbone.Events);
  var opts = _.defaults({}, options, {
    visible: true,
    selectable: true,
    name: "",
  });
  Object.defineProperty(this, "layer", {
    get: _.bind(this.getMapLayer, this),
  });
  Object.defineProperty(this, "children", {
    get: _.bind(this.getChildren, this),
  });
  Object.defineProperty(this, "name", {
    get: _.bind(this.getName, this),
  });
  Object.defineProperty(this, "fields", {
    get: _.bind(this.getFields, this),
  });
  Object.defineProperty(this, "visible", {
    get: _.bind(this.isVisible, this),
    set: _.bind(this.setVisible, this),
  });

  this._name = opts.name;
  this._visible = opts.visible;
  this._selectable = opts.selectable;

  this.initialize(options);
}

MapLayerBase.prototype = {
  /**
   * Inheritors may use this function to execute additional instructions during object construction.
   * @param  {object} options The options map sent to the constructor.
   */
  initialize: function (/*options*/) {},

  /**
   * Loads the underlying data.
   * @async
   * @return {Promise} A promise object which completes when loading completes and data is ready. The promise shall resolve with a reference to `this` object and the `layer` property of this object.
   */
  load: function () {
    var deferred = new $.Deferred();

    deferred.resolve(this, this.layer);

    return deferred.promise();
  },

  /**
   * Initiate a reload of the underlying data. Useful when lots of things have changed.
   * @async
   * @return {Promise} A promise object which completes when loading completes and data is ready. The promise shall resolve with a reference to `this` object and the `layer` property of this object.
   */
  reload: function () {
    return this.load();
  },

  /**
   * Attach this layer to the given map view.
   * @param  {Map} map The map to which to attach.
   * @return {Promise}         A promise object which shall resolve, if no error, with a reference to `this` object and the `layer` property of this object, once attachment is complete.
   */
  attach: function (map) {
    var promise = this.load();

    promise.done(function (self, layer) {
      if (layer) {
        layer.setVisibility(self.visible && self.areParentsVisible());
        self.trigger("attach");
      }
    });

    return promise;
  },

  /**
   * Gets a value indicating whether or not all of this layer's parent layers are visible. This calculation includes any associated `serviceLayer`.
   */
  areParentsVisible: function () {
    var currentLayer = this.parent;
    var visibility = !this.serviceLayer || this.serviceLayer.visible;

    while (currentLayer && visibility) {
      visibility = visibility && currentLayer.visible;
      currentLayer = currentLayer.parent;
    }

    return visibility;
  },

  /**
   * Detach this layer from the given map.
   * @param  {Map} map The map from which to detach.
   */
  detach: function (map) {},

  /**
   * Gets the ESRI layer represented by this object.
   * @return {esri/layers/GraphicsLayer | esri/layers/FeatureLayer} The core layer. If this is a parent layer, this value will be null.
   */
  getMapLayer: function () {
    return null;
  },

  /**
   * Gets the list of immediate children of this layer.
   * @return {Array} An array of `MapLayerBase` corresponding this layer's children.
   */
  getChildren: function () {
    return [];
  },

  /**
   * Calculates and returns the full geometric extent of the layer.
   * @return {esri/geometry/Extent} The layer extent.
   */
  getExtent: function () {
    return null;
  },

  /**
   * Gets a user-friendly name for this layer.
   * @return {string} The layer name.
   */
  getName: function () {
    return this._name;
  },

  /**
   * Gets the list of fields to use to display metadata of items in this layer.
   * @return {Array} The array of `Field` models.
   */
  getFields: function () {
    return [];
  },

  /**
   * Gets the field with the specified name from the list of fields of this layer.
   * @param  {string} name The name for which to search.
   * @return {Field}      The Field with the given name, or null, if no field is found.
   */
  getField: function (name) {
    return _.find(this.getFields(), function (field) {
      return name == field.id;
    });
  },

  /**
   * Gets the id field for items in the layer. This is not necessarily a member of the collection returned by `getFields`.
   * @return {Field} The id field.
   */
  getIdField: function () {
    return null;
  },

  /**
   * Gets a unique ID for the given model, an item in this layer.
   * @param  {object} model An item in this layer of which to get the ID.
   * @return {mixed}       The object's id.
   */
  getId: function (model) {
    return null;
  },

  /**
   * Gets the display name field for items in the layer. This is not necessarily a member of the collection returned by `getFields`.
   * @return {Field} The id field.
   */
  getDisplayNameField: function () {
    return null;
  },

  /**
   * Gets a display name for the given model, an item in this layer.
   * @param  {object} model An item in this layer of which to get the name.
   * @return {mixed}       The object's name.
   */
  getDisplayName: function (model) {
    return null;
  },

  /**
   * Gets a value indicating whether or not fields or geometries on this layer can be edited.
   * @return {Boolean} True if editing is allowed, otherwise false.
   */
  isEditable: function () {
    return false;
  },

  /**
   * Gets a value indicating whether or not geometries can be drawn on this layer.
   * @return {Boolean} True if drawing is allowed, otherwise false.
   */
  canDraw: function () {
    return false;
  },

  /**
   * Gets a value indicating whether or not this layer is marked as visible.
   * @return {Boolean} True if visible, otherwise false.
   */
  isVisible: function () {
    return this._visible;
  },

  /**
   * Gets a value indicating whether or not there are children present in this layer.
   * @return {Boolean} True if there is at least one child, otherwise false.
   */
  hasChildren: function () {
    return this.children && this.children.length;
  },

  /**
   * Sets the visibility of this layer. Note that this setting does *not* propagate to child layers.
   * @param {Boolean} visible True if visible, otherwise false.
   */
  setVisible: function (visible) {
    if (this.layer) {
      this.layer.setVisibility(visible);
    }

    this._visible = visible;
    this.trigger("visibility-changed", this, visible);
  },

  /**
   * Gets a value indicating whether or not this layer should support interaction.
   * @return {Boolean} True to support interaction, otherwise false.
   */
  isSelectable: function () {
    return this._selectable;
  },

  /**
   * Sets a value indicating whether or not this layer should support interaction.
   * @param {Boolean} value True to support interaction, otherwise false.
   */
  setSelectable: function (value) {
    this._selectable = value;
    this.trigger("selectability-changed", value);
  },

  /**
   * Marks the layer as invisible.
   */
  hide: function () {
    this.setVisible(false);
  },

  /**
   * Marks the service as visible.
   */
  show: function () {
    this.setVisible(true);
  },

  /**
   * Gets the data model for the given geometry model.
   * @param  {object} geometryModel The geometry model.
   * @return {object}               The data model.
   */
  dataForGeometry: function (geometryModel) {
    //replaces dataForGeometry
    return geometryModel;
  },

  /**
   * Gets the geometry model for the given data model.
   * @param  {object} dataModel The data model.
   * @return {object}           The geometry model.
   */
  geometryForData: function (dataModel) {
    //replaces geometryForData
    return dataModel;
  },

  /**
   * Finds an array of data models which "match" the given label.
   * @param {Graphic} label An `esri.Graphic` serving as a source of the query.
   * @param {Point} [point] An `esri.geometry.Point` serving as an alternate source of the query.
   * @return {Array} An array of models which "match" the given label.
   */
  findModelsFromLabel: function (label, point) {
    return [];
  },

  /**
   * Gets the geometry model for the given input.
   * @param  {mixed} model Either a geometry model or a data model.
   * @return {object}       The geometry model, if it can be derived. Otherwise, null is returned.
   */
  toGeometry: function (model) {
    var geom = this.geometryForData(model);

    if (!geom) {
      //we must have attempted to retrieve a geometry with a geometry
      geom = model;
    }

    if (!geom) {
      geom = null;
    }

    return geom;
  },

  /**
   * Gets the data model for the given input.
   * @param  {mixed} model Either a geometry model or a data model.
   * @return {object}       The data model, if it can be derived. Otherwise, null is returned.
   */
  toData: function (model) {
    var geom = this.toGeometry(model);
    var result = null;

    if (geom) {
      result = this.dataForGeometry(geom);
    }

    return result;
  },

  /**
   * Gets the ESRI graphic for the given input.
   * @param {mixed} model Either a geometry model or a data model.
   * @return {esri/Graphic} The graphic, if it can be derived, otherwise null.
   * @note This should really be specific to the view portion and not in a model at all, but yolo.
   */
  toGraphic: function (model) {
    return model;
  },

  /**
   * Gets the feature associated with the given input.
   * @param model Either a geometry model or a data model associated with this layer.
   * @return The feature, which may be a Feature model or an esri.Graphic. It will have an `originalLayer` property set to this instance.
   * @note This is similar to `toGraphic`, but returns a more model-centric representation.
   */
  toFeature: function (model) {
    model.originalLayer = this;

    return model;
  },

  /**
   * Highlights a set of map objects determined by a selector.
   * @param  {SymbolFactory} symbolFactory The highlight style to use.
   * @param  {mixed} selector      The selector type is implementation-dependent. If not specified, all objects shall be highlighted.
   */
  highlight: function (symbolFactory, selector) {},

  /**
   * Unhighlights a set of map object determined by a selector.
   * @param  {mixed} selector The selector type is implementation-dependent. If not specified, all objects shall be unhighlighted.
   */
  unhighlight: function (selector) {},

  /**
   * Gets a value indicating whether or not a symbol factory can be set on this layer.
   * @return {Boolean} True if a factory can be set, otherwise false.
   */
  hasSymbolFactory: function () {
    return false;
  },

  /**
   * Loads the collections of documents associated with each object in this layer.
   * @return {Promise} A promise object which shall resolve, if no error, with a mapping from layer ID to a mapping from model ID to `DocumentCollection`. If `hasFiles` returns false, this shall return null.
   */
  loadFiles: function () {
    return null;
  },

  /**
   * Gets a value indicating whether or not objects in this layer (can) have files associated with them.
   * @return {Boolean} True if files can be associated, otherwise false.
   */
  hasFiles: function () {
    return false;
  },

  /**
   * Gets the root URL which lists files associated with the given model.
   * @return {string} The root URL. Always null if `hasFiles` returns false.
   */
  getRootFilePath: function (model) {
    return null;
  },

  /**
   * Gets the URL which lists files contained by the given folder model.
   * @param  {Backbone.Model} folderModel The folder data.
   * @return {string}             The URL which lists the requested files. Always null if `hasFiles` returns false.
   */
  getChildDirectoryPath: function (folderModel) {
    return null;
  },

  /**
   * Gets the URL which can be used to access the file associated with the given file model.
   * @param  {Backbone.Model} fileModel The file model.
   * @return {string}           THe URL which contains the file data. Always null if `hasFiles` returns false.
   */
  getChildFilePath: function (fileModel) {
    return null;
  },

  /**
   * Load the data model with the given ID.
   * @param  {mixed} modelId The id of the data model to load.
   * @return {Promise}         A promise which shall, on completion, resolve with the data model identified or reject with an error.
   */
  loadModel: function (modelId) {
    return null;
  },

  /**
   * Generate a summary of the layer.
   * @return {Object} The summary result.
   */
  summarize: function () {
    return {
      summary_fields: [],
    };
  },

  /**
   * Gets the model which provides the data for this model.
   * @return {Backbone.Model} The model instance.
   * @abstract
   */
  getWrappedModel: function () {
    return null;
  },

  /**
   * Gets a value indicating whether or not this layer supports exporting of data.
   * @returns {Boolean} True if export is supported, otherwise false.
   */
  canExport: function () {
    return false;
  },
};

/**
 * Backbone-style extend function.
 * @function
 * @name extend
 * @see util/extend
 * @scope instance
 * @memberOf module:app/models/map/base/MapLayerBase
 */
MapLayerBase.extend = extend;

export default MapLayerBase;



//////////////////
// WEBPACK FOOTER
// ./public/js/app/models/map/base/MapLayerBase.js
// module id = 357
// module chunks = 1